import { Box, Card, List, ListItem, MenuItem, Select, TextField, Typography, Checkbox, Alert } from '@mui/material';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { configureCustomFieldConfigs } from './adminPageRequest';
import { LoadingButton } from '@mui/lab';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles((theme) => ({
  container: {
    height: '80vh',
    width: '100%',
    padding: '1rem',
    marginLeft: '5rem',
    marginTop: '1rem',
    background: '#F5F5F5',
    borderRadius: '1rem',
    overflow: 'scroll',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '1rem',
  },
  select: {
    height: '40px',
  },
  instructionAlert: {
    marginBottom: '1rem',
  },
  listWrapperCard: {
    minWidth: '485px',
  },
  addListItemButton: {
    width: '80%',
    margin: '0.5rem',
  },
}));

interface CustomFieldsContainerProps {
  expertCustomFields: CustomFieldConfig[];
  journalistCustomFields: CustomFieldConfig[];
  outletCustomFields: CustomFieldConfig[];
  communityMemberCustomFields: CustomFieldConfig[];
  organizationCustomFields: CustomFieldConfig[];
}

type CustomFieldConfigAction = 'add' | 'update' | 'remove';
interface CustomFieldConfig {
  action?: CustomFieldConfigAction;
  fieldKey: string;
  label: string;
  enabled: boolean;
}

interface CustomFieldsConfigCardProps {
  isEdit: boolean;
  index: number;
  key: string;
  configData: CustomFieldConfig;
  onLabelChange: (label: string, fieldKey: string) => void;
  onToggleEnabled: (enabled: boolean, fieldKey: string) => void;
  onRemove: (fieldKey: string) => void;
}

const CustomFieldsConfigListItem = ({
  isEdit,
  index,
  key,
  configData,
  onLabelChange,
  onToggleEnabled,
  onRemove,
}: CustomFieldsConfigCardProps) => {
  if (!isEdit) {
    return (
      <ListItem key={key} divider={true}>
        <Box m={2} gap={2} display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Typography fontWeight={600} fontSize={18} data-cy={`config-label-${index}`}>
            {configData.label}
          </Typography>
          <Typography fontSize={18} data-cy={`config-enabled-${index}`}>
            {configData.enabled ? 'Enabled' : 'Disabled'}
          </Typography>
        </Box>
      </ListItem>
    );
  }

  return (
    <ListItem key={key} divider={true}>
      <Box display="flex" m={2} gap={2}>
        <Box>
          <TextField
            data-cy={`config-label-textfield-${index}`}
            placeholder="Label"
            value={configData.label}
            onChange={(e) => onLabelChange(e.target.value, configData.fieldKey)}
          />
        </Box>
        <Box display="flex" alignItems="center">
          <Checkbox
            data-cy={`config-enabled-checkbox-${index}`}
            checked={configData.enabled}
            onChange={(e) => onToggleEnabled(e.target.checked, configData.fieldKey)}
          />
          <Typography>{configData.enabled ? 'Enabled' : 'Disabled'}</Typography>
        </Box>
        <Button
          data-cy={`config-remove-btn-${index}`}
          color="error"
          variant="outlined"
          onClick={() => onRemove(configData.fieldKey)}
        >
          Delete
        </Button>
      </Box>
    </ListItem>
  );
};

const NEW_CONFIG_PREFIX_REGEX = /^new_config_\d+$/;
const CONFIG_LIMIT = 5;

const CustomFieldsContainer = ({
  expertCustomFields,
  journalistCustomFields,
  outletCustomFields,
  communityMemberCustomFields,
  organizationCustomFields,
}: CustomFieldsContainerProps) => {
  const classes = useStyles();

  const [selectedModel, setSelectedModel] = useState<string>('Journalist');
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [expertConfigs, setExpertConfigs] = useState<CustomFieldConfig[]>(expertCustomFields);
  const [journalistConfigs, setJournalistConfigs] = useState(journalistCustomFields);
  const [outletConfigs, setOutletConfigs] = useState(outletCustomFields);
  const [communityMemberConfigs, setCommunityMemberConfigs] = useState(communityMemberCustomFields);
  const [organizationConfigs, setOrganizationConfigs] = useState(organizationCustomFields);

  const [removingExpertConfigs, setRemovingExpertConfigs] = useState<string[]>([]);
  const [removingJournalistConfigs, setRemovingJournalistConfigs] = useState<string[]>([]);
  const [removingOutletConfigs, setRemovingOutletConfigs] = useState<string[]>([]);
  const [removingCommunityMemberConfigs, setRemovingCommunityMemberConfigs] = useState<string[]>([]);
  const [removingOrganizationConfigs, setRemovingOrganizationConfigs] = useState<string[]>([]);

  const queryClient = useQueryClient();

  const resetCustomFieldConfigsStates = () => {
    setRemovingExpertConfigs([]);
    setRemovingJournalistConfigs([]);
    setRemovingOutletConfigs([]);
    setRemovingCommunityMemberConfigs([]);
    setRemovingOrganizationConfigs([]);

    setExpertConfigs(expertCustomFields);
    setJournalistConfigs(journalistCustomFields);
    setOutletConfigs(outletCustomFields);
    setCommunityMemberConfigs(communityMemberCustomFields);
    setOrganizationConfigs(organizationCustomFields);
  };

  const configureExpertConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('Expert', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['expertCustomFields', 'adminPage'] });
    },
  });

  const configureJournalistConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('Journalist', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['journalistCustomFields', 'adminPage'] });
    },
  });

  const configureOutletConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('Org', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['outletCustomFields', 'adminPage'] });
    },
  });

  const configureCommunityMemberConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('CommunityMember', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['communityMemberCustomFields', 'adminPage'] });
    },
  });

  const configureOrganizationConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('Organization', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationCustomFields', 'adminPage'] });
    },
  });

  useEffect(() => {
    setExpertConfigs(expertCustomFields);
    setJournalistConfigs(journalistCustomFields);
    setOutletConfigs(outletCustomFields);
    setCommunityMemberConfigs(communityMemberCustomFields);
    setOrganizationConfigs(organizationCustomFields);
  }, [
    expertCustomFields,
    journalistCustomFields,
    outletCustomFields,
    communityMemberCustomFields,
    organizationCustomFields,
  ]);

  const handleSelectChange = (selected: string) => {
    setSelectedModel(selected);
  };

  const handleConfigureBtnClick = () => {
    setIsEdit(true);
  };

  const getSelectedConfigs = () => {
    let list: CustomFieldConfig[] = [];
    switch (selectedModel) {
      case 'Expert':
        list = expertConfigs;
        break;
      case 'Journalist':
        list = journalistConfigs;
        break;
      case 'Outlet':
        list = outletConfigs;
        break;
      case 'Community Member':
        list = communityMemberConfigs;
        break;
      case 'Organization':
        list = organizationConfigs;
        break;
      default:
        break;
    }
    return list;
  };

  const getAddingConfigs = () => {
    return getSelectedConfigs()
      .filter((c) => c.action === 'add')
      .filter((c) => c.label)
      .map((c) => ({
        label: c.label,
        enabled: c.enabled,
      }));
  };

  const getUpdatingConfigs = () => {
    return getSelectedConfigs()
      .filter((c) => c.action === 'update')
      .map((c) => ({
        fieldKey: c.fieldKey,
        label: c.label,
        enabled: c.enabled,
      }));
  };

  const getRemovingConfigs = () => {
    let list: string[] = [];
    switch (selectedModel) {
      case 'Expert':
        list = removingExpertConfigs;
        break;
      case 'Journalist':
        list = removingJournalistConfigs;
        break;
      case 'Outlet':
        list = removingOutletConfigs;
        break;
      case 'Community Member':
        list = removingCommunityMemberConfigs;
        break;
      case 'Organization':
        list = removingOrganizationConfigs;
        break;
      default:
        break;
    }
    return list.map((k) => ({ fieldKey: k }));
  };

  const handleSaveBtnClick = () => {
    const adds = getAddingConfigs();
    const updates = getUpdatingConfigs();
    const removes = getRemovingConfigs();

    switch (selectedModel) {
      case 'Expert':
        configureExpertConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      case 'Journalist':
        configureJournalistConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      case 'Outlet':
        configureOutletConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      case 'Community Member':
        configureCommunityMemberConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      case 'Organization':
        configureOrganizationConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      default:
        console.error('save failed - unknown model selected');
    }
  };

  const handleCancelBtnClick = () => {
    resetCustomFieldConfigsStates();
    setIsEdit(false);
  };

  const getCustomFieldConfigsListItemLabelChangeEventHandler = () => {
    const configsChangeMapper = (c: CustomFieldConfig, label: string, fieldKey: string): CustomFieldConfig => {
      if (c.fieldKey === fieldKey && NEW_CONFIG_PREFIX_REGEX.test(c.fieldKey)) {
        return {
          ...c,
          action: 'add',
          label: label,
        };
      } else if (c.fieldKey === fieldKey && !NEW_CONFIG_PREFIX_REGEX.test(c.fieldKey)) {
        return {
          ...c,
          action: 'update',
          label: label,
        };
      } else {
        return c;
      }
    };

    switch (selectedModel) {
      case 'Expert':
        return (label: string, fieldKey: string) =>
          setExpertConfigs(expertConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      case 'Journalist':
        return (label: string, fieldKey: string) =>
          setJournalistConfigs(journalistConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      case 'Outlet':
        return (label: string, fieldKey: string) =>
          setOutletConfigs(outletConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      case 'Community Member':
        return (label: string, fieldKey: string) =>
          setCommunityMemberConfigs(communityMemberConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      case 'Organization':
        return (label: string, fieldKey: string) =>
          setOrganizationConfigs(organizationConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      default:
        return (label: string, fieldKey: string) => {
          console.error('unknown selected model, cannot update label');
        };
    }
  };

  const getCustomFieldConfigsListItemToggleEnabledEventHandler = () => {
    const configsChangeMapper = (c: CustomFieldConfig, enabled: boolean, fieldKey: string): CustomFieldConfig => {
      if (c.fieldKey === fieldKey && NEW_CONFIG_PREFIX_REGEX.test(c.fieldKey)) {
        return {
          ...c,
          action: 'add',
          enabled: enabled,
        };
      } else if (c.fieldKey === fieldKey && !NEW_CONFIG_PREFIX_REGEX.test(c.fieldKey)) {
        return {
          ...c,
          action: 'update',
          enabled: enabled,
        };
      } else {
        return c;
      }
    };

    switch (selectedModel) {
      case 'Expert':
        return (enabled: boolean, fieldKey: string) => {
          setExpertConfigs(expertConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      case 'Journalist':
        return (enabled: boolean, fieldKey: string) => {
          setJournalistConfigs(journalistConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      case 'Outlet':
        return (enabled: boolean, fieldKey: string) => {
          setOutletConfigs(outletConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      case 'Community Member':
        return (enabled: boolean, fieldKey: string) => {
          setCommunityMemberConfigs(communityMemberConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      case 'Organization':
        return (enabled: boolean, fieldKey: string) => {
          setOrganizationConfigs(organizationConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      default:
        return (enabled: boolean, fieldKey: string) => {
          console.error('unknown selected model, cannot toggle enabled/disabled');
        };
    }
  };

  const getCustomFieldConfigsListItemRemoveEventHandler = () => {
    switch (selectedModel) {
      case 'Expert':
        return (fieldKey: string) => {
          setExpertConfigs(expertConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingExpertConfigs([...removingExpertConfigs, fieldKey]);
          }
        };
      case 'Journalist':
        return (fieldKey: string) => {
          setJournalistConfigs(journalistConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingJournalistConfigs([...removingJournalistConfigs, fieldKey]);
          }
        };
      case 'Outlet':
        return (fieldKey: string) => {
          setOutletConfigs(outletConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingOutletConfigs([...removingOutletConfigs, fieldKey]);
          }
        };
      case 'Community Member':
        return (fieldKey: string) => {
          setCommunityMemberConfigs(communityMemberConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingCommunityMemberConfigs([...removingCommunityMemberConfigs, fieldKey]);
          }
        };
      case 'Organization':
        return (fieldKey: string) => {
          setOrganizationConfigs(organizationConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingOrganizationConfigs([...removingOrganizationConfigs, fieldKey]);
          }
        };
      default:
        return (fieldKey: string) => {
          console.error('unknown selected model, cannot remove custom field config');
        };
    }
  };

  const handleAddNewConfigClick = () => {
    switch (selectedModel) {
      case 'Expert':
        setExpertConfigs([
          ...expertConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      case 'Journalist':
        setJournalistConfigs([
          ...journalistConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      case 'Outlet':
        setOutletConfigs([
          ...outletConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      case 'Community Member':
        setCommunityMemberConfigs([
          ...communityMemberConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      case 'Organization':
        setOrganizationConfigs([
          ...organizationConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      default:
        break;
    }
  };

  let actionComponent;
  if (isEdit) {
    actionComponent = (
      <Box sx={{ marginTop: '2vh', marginBottom: '2vh' }}>
        <LoadingButton
          loading={configureExpertConfigsMutation.isPending}
          sx={{ marginRight: '1rem' }}
          color="primary"
          variant="contained"
          onClick={() => handleSaveBtnClick()}
          data-cy="save-btn"
        >
          Save
        </LoadingButton>
        <Button
          disabled={configureExpertConfigsMutation.isPending}
          variant="outlined"
          onClick={() => handleCancelBtnClick()}
          data-cy="cancel-btn"
        >
          Cancel
        </Button>
      </Box>
    );
  } else {
    actionComponent = (
      <Button
        color="primary"
        variant="contained"
        sx={{ marginTop: '2vh', marginBottom: '2vh' }}
        onClick={() => handleConfigureBtnClick()}
        data-cy="configure-btn"
      >
        Configure
      </Button>
    );
  }

  const addNewCustomConfigButton = (
    <Button
      className={classes.addListItemButton}
      variant="outlined"
      onClick={() => handleAddNewConfigClick()}
      data-cy="add-btn"
    >
      Add
    </Button>
  );

  return (
    <Box className={classes.container}>
      <Typography variant="h5">Contacts Custom Fields</Typography>
      <Box className={classes.wrapper}>
        <Select
          id="custom-field-config-model-select"
          className={classes.select}
          value={selectedModel}
          onChange={(e) => handleSelectChange(e.target.value)}
        >
          <MenuItem value={'Journalist'}>Journalist</MenuItem>
          <MenuItem value={'Expert'}>Expert</MenuItem>
          <MenuItem value={'Outlet'}>Outlet</MenuItem>
          <MenuItem value={'Community Member'}>Community Member</MenuItem>
          <MenuItem value={'Organization'}>Organization</MenuItem>
        </Select>

        {actionComponent}

        {isEdit && (
          <Alert color="info" className={classes.instructionAlert}>
            <Typography align="left">
              You can have at most <strong>5</strong> custom fields.
            </Typography>
            <Typography align="left">
              Updating the label will not <strong>Reset</strong> custom field values in {selectedModel}.
            </Typography>
            <Typography align="left">
              To reset a custom field, simply remove the one you want to reset and add a new custom field.
            </Typography>
            <Typography align="left">
              Disabled custom fields will be <strong>Hidden</strong> in {selectedModel}.
            </Typography>
          </Alert>
        )}

        {selectedModel === 'Expert' && (
          <Card className={classes.listWrapperCard}>
            <List disablePadding>
              {expertConfigs.map((config, index) => (
                <CustomFieldsConfigListItem
                  isEdit={isEdit}
                  index={index}
                  key={config.fieldKey}
                  configData={config}
                  onLabelChange={getCustomFieldConfigsListItemLabelChangeEventHandler()}
                  onToggleEnabled={getCustomFieldConfigsListItemToggleEnabledEventHandler()}
                  onRemove={getCustomFieldConfigsListItemRemoveEventHandler()}
                />
              ))}
              {expertConfigs.length < CONFIG_LIMIT && isEdit && addNewCustomConfigButton}
            </List>
          </Card>
        )}

        {selectedModel === 'Journalist' && (
          <Card className={classes.listWrapperCard}>
            <List disablePadding>
              {journalistConfigs.map((config, index) => (
                <CustomFieldsConfigListItem
                  isEdit={isEdit}
                  index={index}
                  key={config.fieldKey}
                  configData={config}
                  onLabelChange={getCustomFieldConfigsListItemLabelChangeEventHandler()}
                  onToggleEnabled={getCustomFieldConfigsListItemToggleEnabledEventHandler()}
                  onRemove={getCustomFieldConfigsListItemRemoveEventHandler()}
                />
              ))}
              {journalistConfigs.length < CONFIG_LIMIT && isEdit && addNewCustomConfigButton}
            </List>
          </Card>
        )}

        {selectedModel === 'Outlet' && (
          <Card className={classes.listWrapperCard}>
            <List disablePadding>
              {outletConfigs.map((config, index) => (
                <CustomFieldsConfigListItem
                  isEdit={isEdit}
                  index={index}
                  key={config.fieldKey}
                  configData={config}
                  onLabelChange={getCustomFieldConfigsListItemLabelChangeEventHandler()}
                  onToggleEnabled={getCustomFieldConfigsListItemToggleEnabledEventHandler()}
                  onRemove={getCustomFieldConfigsListItemRemoveEventHandler()}
                />
              ))}
              {outletConfigs.length < CONFIG_LIMIT && isEdit && addNewCustomConfigButton}
            </List>
          </Card>
        )}

        {selectedModel === 'Community Member' && (
          <Card className={classes.listWrapperCard}>
            <List disablePadding>
              {communityMemberConfigs.map((config, index) => (
                <CustomFieldsConfigListItem
                  isEdit={isEdit}
                  index={index}
                  key={config.fieldKey}
                  configData={config}
                  onLabelChange={getCustomFieldConfigsListItemLabelChangeEventHandler()}
                  onToggleEnabled={getCustomFieldConfigsListItemToggleEnabledEventHandler()}
                  onRemove={getCustomFieldConfigsListItemRemoveEventHandler()}
                />
              ))}
              {communityMemberConfigs.length < CONFIG_LIMIT && isEdit && addNewCustomConfigButton}
            </List>
          </Card>
        )}

        {selectedModel === 'Organization' && (
          <Card className={classes.listWrapperCard}>
            <List disablePadding>
              {organizationConfigs.map((config, index) => (
                <CustomFieldsConfigListItem
                  isEdit={isEdit}
                  index={index}
                  key={config.fieldKey}
                  configData={config}
                  onLabelChange={getCustomFieldConfigsListItemLabelChangeEventHandler()}
                  onToggleEnabled={getCustomFieldConfigsListItemToggleEnabledEventHandler()}
                  onRemove={getCustomFieldConfigsListItemRemoveEventHandler()}
                />
              ))}
              {organizationConfigs.length < CONFIG_LIMIT && isEdit && addNewCustomConfigButton}
            </List>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default CustomFieldsContainer;
